export type VoiceRecorderStopReason = 'silence' | 'manual' | 'timeout' | 'abort';

export type VoiceRecordingResult = {
  blob: Blob;
  mimeType: string;
  reason: VoiceRecorderStopReason;
};

function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

function extensionForMime(mimeType: string): string {
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('wav')) return 'wav';
  return 'webm';
}

export function voiceBlobFilename(mimeType: string): string {
  return `speech.${extensionForMime(mimeType)}`;
}

/**
 * Enregistre le micro jusqu’à silence, arrêt manuel ou délai max.
 * Utilise MediaRecorder (fiable) plutôt que la Web Speech API du navigateur.
 */
export async function recordVoiceUntilStop(opts: {
  silenceMs?: number;
  maxMs?: number;
  initialNoSpeechMs?: number;
  onLevel?: (level: number) => void;
  registerStop?: (stop: () => void) => void;
}): Promise<VoiceRecordingResult> {
  const silenceMs = opts.silenceMs ?? 4000;
  const maxMs = opts.maxMs ?? 60_000;
  const initialNoSpeechMs = opts.initialNoSpeechMs ?? 25_000;
  const mimeType = pickRecorderMimeType() ?? 'audio/webm';

  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks: Blob[] = [];
  let stopReason: VoiceRecorderStopReason = 'manual';
  let stopped = false;
  let heardSpeech = false;
  let lastSpeechAt = Date.now();
  const startedAt = Date.now();
  let raf = 0;

  const audioCtx = new AudioContext();
  const analyser = audioCtx.createAnalyser();
  analyser.fftSize = 256;
  const source = audioCtx.createMediaStreamSource(stream);
  source.connect(analyser);
  const freq = new Uint8Array(analyser.frequencyBinCount);

  const cleanup = () => {
    cancelAnimationFrame(raf);
    stream.getTracks().forEach((track) => track.stop());
    void audioCtx.close().catch(() => undefined);
  };

  const stopRecording = (reason: VoiceRecorderStopReason) => {
    if (stopped) return;
    stopped = true;
    stopReason = reason;
    try {
      if (recorder.state !== 'inactive') recorder.stop();
    } catch {
      cleanup();
    }
  };

  opts.registerStop?.(() => stopRecording('manual'));

  return new Promise<VoiceRecordingResult>((resolve, reject) => {
    recorder.ondataavailable = (ev) => {
      if (ev.data.size > 0) chunks.push(ev.data);
    };
    recorder.onerror = () => {
      cleanup();
      reject(new Error('media-recorder-error'));
    };
    recorder.onstop = () => {
      cleanup();
      const outMime = recorder.mimeType || mimeType;
      resolve({
        blob: new Blob(chunks, { type: outMime }),
        mimeType: outMime,
        reason: stopReason,
      });
    };

    const monitor = () => {
      if (stopped) return;
      analyser.getByteFrequencyData(freq);
      let sum = 0;
      for (let i = 0; i < freq.length; i++) sum += freq[i];
      const avg = sum / freq.length;
      opts.onLevel?.(Math.min(1, avg / 80));

      if (avg > 10) {
        heardSpeech = true;
        lastSpeechAt = Date.now();
      }

      const elapsed = Date.now() - startedAt;
      if (elapsed >= maxMs) {
        stopRecording('timeout');
        return;
      }

      if (heardSpeech && Date.now() - lastSpeechAt >= silenceMs) {
        stopRecording('silence');
        return;
      }

      if (!heardSpeech && elapsed >= initialNoSpeechMs) {
        stopRecording('silence');
        return;
      }

      raf = requestAnimationFrame(monitor);
    };

    try {
      recorder.start(250);
      raf = requestAnimationFrame(monitor);
    } catch (e) {
      cleanup();
      reject(e);
    }
  });
}

export function isVoiceCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'
  );
}
